/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { ButtonWithIcon } from '@els/els-react--button';
import { Flyout } from '@els/els-react--flyout';
import HoverTooltip from '@Components/HoverTooltip';
import AppStorage from '@Services/AppStorage';
import { dispatchOpenSaveFormModal } from '@Containers/SaveFormModal/SaveFormModalActions';
import { useDispatch, useSelector } from 'react-redux';
import noop from 'lodash/noop';
import * as ControlsHelper from './ControlsHelper';
import { resetNodeValueAction, resetSearchBuilderAction, addToBuilderAction, getFormsAction } from '../QueryletActions';
import FlyoutContent from '../Shared/FlyoutContent';
import { SAVE_OPTIONS, BUTTON_CONFIG } from './constants';
import './Controls.scss';
import { forms } from '../QuerySelector';

function Controls({ nodesList, properties, favList, hiddenList, importedQuery, importedQueryStateParamValue }) {
  const dispatch = useDispatch();
  const formsList = useSelector(forms);
  const [isOpen, setIsOpen] = useState(false);
  const [isIconOnly, setIsIconOnly] = useState(false);
  const [addedName, setAddedName] = useState(null);
  const [actionMessage, setActionMessage] = useState(null);
  const actionMessageTimerRef = useRef(null);
  const addedNameTimerRef = useRef(null);
  const buttonConfig = BUTTON_CONFIG(nodesList);
  const saveOptions = SAVE_OPTIONS;

  const responsiveIcon = (breakpoint = 1400) => {
    const mediaQuery = window.matchMedia(`(max-width: ${breakpoint}px)`);

    return mediaQuery.matches;
  };

  const result = responsiveIcon();

  useEffect(() => {
    setIsIconOnly(result);
  }, [result]);

  const toggleDropdown = useRef(false);
  const saveTriggerRef = useRef(null);

  useEffect(() => {
    if ((importedQuery || importedQueryStateParamValue) && properties && properties.length) {
      const importedQueryData = importedQuery || importedQueryStateParamValue;
      ControlsHelper.importer(nodesList, properties, importedQueryData.queryFactItem || importedQueryData, {
        context: importedQueryData.context,
        isSemanticSearchIncluded: importedQueryData.isSemanticSearchIncluded,
        replaceExistingQuery: importedQueryData.replaceExistingQuery,
        ...(importedQueryData.resolvedByCasApi && { resolvedByCasApi: importedQueryData.resolvedByCasApi }),
      });
    }
  }, [importedQuery, importedQueryStateParamValue, properties]);

  useEffect(
    () => () => {
      if (actionMessageTimerRef.current) {
        clearTimeout(actionMessageTimerRef.current);
      }
      if (addedNameTimerRef.current) {
        clearTimeout(addedNameTimerRef.current);
      }
    },
    []
  );

  const announceAction = (message) => {
    setActionMessage(message);
    if (actionMessageTimerRef.current) {
      clearTimeout(actionMessageTimerRef.current);
    }
    actionMessageTimerRef.current = setTimeout(() => {
      setActionMessage(null);
      actionMessageTimerRef.current = null;
    }, 1000);
  };

  function updatebasedonIcon(label) {
    switch (label) {
      case 'redo':
        announceAction('Resetting query fields');
        return dispatch(resetNodeValueAction(nodesList));
      case 'trash':
        announceAction('Query builder cleared');
        return dispatch(resetSearchBuilderAction(nodesList, properties, hiddenList));

      // For the SaveFile case, the custom toggleIsOpen functionality has been removed
      // because the flyout now manages its own state through onToggle.
      // And returning undefine explicitly to avoid conflict with default case

      case 'saveFile':
        return undefined;
      default:
        announceAction('Query imported');
        return ControlsHelper.importQuery();
    }
  }

  function openSaveFormModal() {
    dispatchOpenSaveFormModal({
      forms:
        formsList.filter((form) => form.displayName === 'My Forms')[0] &&
        formsList.filter((form) => form.displayName === 'My Forms')[0].children,
      refreshForms: () => {
        dispatch(getFormsAction());
      },
    });
  }

  const handleAddToBuilder = (config) => {
    dispatch(addToBuilderAction(config, 'default'));
    setAddedName(config.fullName || config.shortName);
    if (addedNameTimerRef.current) {
      clearTimeout(addedNameTimerRef.current);
    }
    addedNameTimerRef.current = setTimeout(() => {
      setAddedName(null);
      addedNameTimerRef.current = null;
    }, 1000);
  };

  const onSaveChange = ({ id }) => {
    AppStorage.instance.set('qbNodesList', nodesList);
    if (id.toLowerCase() === 'savequery') {
      ControlsHelper.saveToFile('name', nodesList);
    } else {
      openSaveFormModal();
    }
    setIsOpen(false);
    setTimeout(() => saveTriggerRef.current?.querySelector('button')?.focus(), 0);
  };

  return (
    <div className="qb-control-container" role="toolbar" aria-label="Query Builder Tools">
      <div className="qb-control-container__left_container">
        <div aria-live="polite" aria-atomic="true">
          {actionMessage && <span className="sr-only">{actionMessage}</span>}
        </div>
        {buttonConfig.map((config) =>
          config.dropdown && !config.disabled ? (
            <div key={config.id} className="qb-control-save-button-wrapper" ref={saveTriggerRef}>
              <HoverTooltip theme="simple" tooltip={config?.ally?.name} tooltipPlace="bottom">
                <Flyout
                  ref={toggleDropdown}
                  isControlled
                  isOpen={isOpen}
                  onToggle={(visible) => setIsOpen(visible)}
                  flyout={<FlyoutContent options={saveOptions} key={config.id} onSaveChange={onSaveChange} />}
                  shouldCloseOnESC
                  role="dialog"
                  placement="bottom-start"
                  pointerType="primary"
                  trigger="click">
                  <ButtonWithIcon
                    type="borderless"
                    size="small"
                    layout="vertical"
                    sprite={config.label}
                    data-e2e={config.automationId}
                    id={config.id}
                    active={isOpen}
                    iconOnly={isIconOnly}
                    disabled={config.disabled}
                    onClick={noop}>
                    {config?.ally?.name}
                  </ButtonWithIcon>
                </Flyout>
              </HoverTooltip>
            </div>
          ) : (
            <HoverTooltip key={config.id} theme="simple" tooltip={config?.ally?.name} tooltipPlace="bottom">
              <ButtonWithIcon
                key={config.id}
                type="borderless"
                size="small"
                id={config.id}
                data-e2e={config.automationId}
                layout="vertical"
                sprite={config.label}
                iconOnly={isIconOnly}
                onClick={() => updatebasedonIcon(config.id)}
                disabled={config.disabled}>
                {config?.ally?.name}
              </ButtonWithIcon>
            </HoverTooltip>
          )
        )}
      </div>
      <div className="qb-control-container__right_container">
        <div aria-live="polite" aria-atomic="true">
          {addedName && <span className="sr-only">{addedName} added to query builder</span>}
        </div>
        {favList.map((config) =>
          config.label !== 'Pixel' ? (
            <Flyout
              key={config.id}
              flyout={<span aria-hidden="true">{config.fullName ? config.fullName : config.shortName}</span>}
              shouldCloseOnESC
              theme="simple"
              trigger="hover">
              <ButtonWithIcon
                key={config.id}
                type="borderless"
                size="small"
                id={config.id}
                layout="vertical"
                sprite={config.iconId}
                data-e2e={config.automationId}
                iconOnly={isIconOnly}
                aria-label={config.fullName || config.shortName}
                onClick={() => handleAddToBuilder(config)}
                disabled={config.hidden}>
                <span aria-hidden="true">{config.shortName}</span>
              </ButtonWithIcon>
            </Flyout>
          ) : (
            <Flyout
              key={config.id}
              flyout={<span aria-hidden="true">{config.fullName ? config.fullName : config.shortName}</span>}
              shouldCloseOnESC
              theme="simple"
              trigger="hover">
              <ButtonWithIcon
                key={config.id}
                type="borderless"
                size="small"
                id={config.id}
                data-e2e={config.automationId}
                layout="vertical"
                sprite={config.iconId}
                iconOnly={isIconOnly}
                className="c-els-link--with-icon c-els-link-vertical-temp"
                aria-label={config.fullName || config.shortName}
                onClick={() => handleAddToBuilder(config)}
                disabled={config.hidden}>
                <span aria-hidden="true">{config.shortName}</span>
              </ButtonWithIcon>
            </Flyout>
          )
        )}
      </div>
    </div>
  );
}
Controls.propTypes = {
  nodesList: PropTypes.array,
  properties: PropTypes.array,
  hiddenList: PropTypes.array,
  importedQuery: PropTypes.any,
  importedQueryStateParamValue: PropTypes.any,
  favList: PropTypes.array,
};
export default Controls;
